import React, {ChangeEvent, Component, ReactNode} from 'react'
import {AsyncCommunicationProps, withAsyncCommunication} from '../shared/hoc/withAsyncCommunication'
import {Information} from '../../network/response/Information'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AssocStatus} from './model/AssocStatus'
import {AdminPageHeadingArea} from '../shared/pageBaseComponents/AdminPageHeadingArea'
import styled from 'styled-components'
import { Box, DialogContent, DialogTitle, DialogActions } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {BackLink} from '../../styledComponents/adminPage/Links'
import {AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {RowFlexContainer} from '../../styledComponents/FlexComponents'
import {Nullable} from '../../app/TypeHelpers'
import AssocManagementRepository from './AssocManagementRepository'
import {MESSAGE_TYPE} from '../../CodeMaster'
import PPTextField from '../shared/forms/PPTextField'
import {Messages} from '../../Messages'

interface Props {
    assocStatus: Nullable<AssocStatus>
    assocManagementRepo: AssocManagementRepository
    onSubmit: (information: Information) => void
    onCancel: () => void
    eventEmitter: EventEmitterExAdmin
}

interface State {
    isCompleted: boolean
    content: string
}

class AdminAssocStatusMemoEditDialog extends Component<Props & AsyncCommunicationProps, State> {
    eventEmitterForDialog: EventEmitterExAdmin

    constructor(props: Readonly<Props & AsyncCommunicationProps>) {
        super(props)

        this.eventEmitterForDialog = new EventEmitterExAdmin()
        this.state = {
            isCompleted: false,
            content: '',
        }

        this.handleChaneText = this.handleChaneText.bind(this)
        this.handleClickSubmit = this.handleClickSubmit.bind(this)
        this.handleClickCancel = this.handleClickCancel.bind(this)
        this.handleRegisterOKClick = this.handleRegisterOKClick.bind(this)
        this.registerAssocStatusMemo = this.registerAssocStatusMemo.bind(this)
        this.updateAssocStatusMemo = this.updateAssocStatusMemo.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        let content: string = ''
        if (this.props.assocStatus !== null &&
            this.props.assocStatus.assocStatusMemo !== null &&
            this.props.assocStatus.assocStatusMemo.content !== null) {
            content = this.props.assocStatus.assocStatusMemo.content
        }
        this.setState({
            isCompleted: true,
            content: content,
        })
        await this.props.eventEmitter.closeLoading()
    }

    handleClickCancel(): void {
        this.props.onCancel()
    }

    async registerAssocStatusMemo(): Promise<void> {
        const {eventEmitter, assocManagementRepo, asyncCallFunction} = this.props
        const {content} = this.state

        if (this.props.assocStatus == null) {
            return
        }
        const {condominiumCd, assocId} = this.props.assocStatus

        await eventEmitter.showLoading()
        try {
            const registerResult: Nullable<Information> = await asyncCallFunction(
                assocManagementRepo.registerAssocStatusMemo.bind(
                    assocManagementRepo,
                    condominiumCd,
                    assocId,
                    content,
                ),
                (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError.responseBody.requestItemErrors.length > 0) {
                        httpErrorOrNetworkError.broadcast(this.eventEmitterForDialog)
                    } else {
                        httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                        this.props.onCancel()
                    }
                },
            )
            if (registerResult !== null) {
                this.props.onSubmit(registerResult)
            }
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async updateAssocStatusMemo(): Promise<void> {
        const {eventEmitter, assocManagementRepo, asyncCallFunction} = this.props
        const {content} = this.state

        if (this.props.assocStatus == null || this.props.assocStatus.assocStatusMemo == null) {
            return
        }
        const {condominiumCd, assocId} = this.props.assocStatus

        await eventEmitter.showLoading()
        try {
            const updateResult: Nullable<Information> = await asyncCallFunction(
                assocManagementRepo.updateAssocStatusMemo.bind(
                    assocManagementRepo,
                    condominiumCd,
                    assocId,
                    content,
                    this.props.assocStatus.assocStatusMemo.version,
                ),
                (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError.responseBody.requestItemErrors.length > 0) {
                        httpErrorOrNetworkError.broadcast(this.eventEmitterForDialog)
                    } else {
                        httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                        this.props.onCancel()
                    }
                },
            )
            if (updateResult !== null) {
                this.props.onSubmit(updateResult)
            }
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleRegisterOKClick(): void {
        this.props.eventEmitter.closeDialog()
        if (this.props.assocStatus == null) {
            return
        }
        if (this.props.assocStatus.assocStatusMemo == null) {
            this.registerAssocStatusMemo()
        } else {
            this.updateAssocStatusMemo()
        }
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleClickSubmit(): Promise<void> {
        if (this.props.assocStatus == null) {
            return
        }

        this.props.eventEmitter.clearBroadcastedErrors()
        this.eventEmitterForDialog.clearBroadcastedErrors()
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: MESSAGE_TYPE.INFORMATION,
                message: Messages.IOPCC0012.toString('メモ'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOKClick,
                    },
                ],
            },
        )
    }

    handleChaneText(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            content: event.target.value,
        })
    }

    render(): ReactNode {
        if (this.props.assocStatus == null) {
            return <></>
        }
        if (!this.state.isCompleted) {
            return <></>
        }

        const {assocName} = this.props.assocStatus
        const {content} = this.state
        let dialogTiele
        if (assocName.length <= 22) {
            dialogTiele = assocName + '　編集'
        } else {
            dialogTiele = assocName.substring(0, 22) + '⋯　編集'
        }
        return <>
            <DialogArea>
                <StyledDialogTitle>
                    <AdminPageHeadingArea
                        title={dialogTiele}
                    />
                </StyledDialogTitle>
                <StyledDialogContents>
                    <span id={'input-memo'}>
                        メモ
                        <Box height={'8px'}/>
                        <MemoContentArea
                            InputProps={{
                                inputProps: {
                                    name: 'content',
                                    maxLength: 200,
                                    minRows: 6,
                                    placeholder: '管理組合に関する補足事項を記載することができます。',
                                },
                            }}
                            fullWidth
                            multiline
                            value={content}
                            eventKeys={['content']}
                            eventEmitter={this.eventEmitterForDialog}
                            onChange={this.handleChaneText}
                        />
                    </span>
                </StyledDialogContents>
                <StyledDialogActions>
                    <PageFooterAreaLeft>
                        <BackLink onClick={this.handleClickCancel}>
                            キャンセル
                        </BackLink>
                    </PageFooterAreaLeft>
                    <PageFooterAreaRight>
                        <AdminPrimaryButton onClick={this.handleClickSubmit}>
                            登録する
                        </AdminPrimaryButton>
                    </PageFooterAreaRight>
                </StyledDialogActions>
            </DialogArea>
        </>
    }
}

export default withAsyncCommunication(AdminAssocStatusMemoEditDialog)

const DialogArea = styled.div`
    width: 720px;
`

const StyledDialogTitle = withStyles({
    root: {
        padding: '24px 24px 0 24px',
    },
})(DialogTitle)

const StyledDialogContents = withStyles({
    root: {
        padding: '6px 24px 12px 24px',
    },
})(DialogContent)

const StyledDialogActions = withStyles({
    root: {
        padding: '12px 24px 24px',
    },
})(DialogActions)

const PageFooterAreaLeft = styled(RowFlexContainer)`
    justify-content: flex-start;
    flex-grow: 1;
    align-items: center;
    order: 1;
`

const PageFooterAreaRight = styled(RowFlexContainer)`
    justify-content: flex-end;
    flex-grow: 1;
    align-items: center;
    order: 2;
`

const MemoContentArea = styled(PPTextField)`
    .MuiInputBase-root {
        font-size: 14px;
        padding: 0;
    }
    height: 108px;
    overflow: auto;
`
